import { forwardRef, useCallback, useEffect, useImperativeHandle, useLayoutEffect, useRef, useState } from "react";
import { createWheelGate } from "../utils/wheelInput";
import { isTouchDevice } from "../utils/viewport.js";

const DEBOUNCE_MS = 100;
const SLIDE_MS = 350;
// How long a slot waits for its parent to follow a change it announced
// (see selfNavigated) before showing whatever the parent says instead.
const SELF_NAV_GRACE_MS = 1200;

const SlotFilter = forwardRef(function SlotFilter({ filters, activeIndex, onChangeIndex, onClickCurrent, enabled, showBorder, loading, itemHeight: itemHeightProp, className, instant, slot3d, horizontalOnly, stacked, stuck, initialSlide, slideMs: slideMsProp, wheelNav = true }, ref) {
  const scrollRef = useRef(null);
  const containerRef = useRef(null);
  const itemHeight = itemHeightProp ?? 32;
  const [isScrolling, setIsScrolling] = useState(false);
  const debounceRef = useRef(null);
  const scrollTimeoutRef = useRef(null);
  const programmatic = useRef(false);

  // ── Slide state (slot3d mode) ──
  const [displayIndex, setDisplayIndex] = useState(activeIndex);
  // A one-off slide length (ms) for a programmatic triggerSlide(dir, ms), or
  // null for the usual SLIDE_MS (stacked mode only).
  const [slideOverrideMs, setSlideOverrideMs] = useState(null);
  const [slide, setSlide] = useState(null); // { to, dir, id }
  const slideIdRef = useRef(0);
  const slideCooldown = useRef(false);
  const [flashDir, setFlashDir] = useState(null); // "up" | "down"
  const flashTimeout = useRef(null);
  // Set when the slot has told its parent to move (onChangeIndex) and is
  // waiting for activeIndex to follow. The sync effect below holds off while
  // it is set: the parent can take a beat to catch up, and snapping back to
  // its stale index in that beat showed the OLD title again before the new
  // one landed a second time. Cleared when activeIndex arrives, or by the
  // grace timer in announce() if it never does.
  const selfNavigated = useRef(false);
  const selfNavTimeout = useRef(null);
  const activeIndexRef = useRef(activeIndex);
  activeIndexRef.current = activeIndex;
  const [hideArrows, setHideArrows] = useState(false);
  const hideArrowsTimeout = useRef(null);
  const sizerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(null);
  // Stacked mode measures its own active item — see the effect below.
  const stackRef = useRef(null);
  const [stackWidth, setStackWidth] = useState(null);
  const [activeWidth, setActiveWidth] = useState(null);
  const outerRef = useRef(null);
  const [isHovered, setIsHovered] = useState(false);
  const programmaticSlide = useRef(false);
  const [showInitialSlide] = useState(!!initialSlide);

  // Sync displayIndex when activeIndex changes externally (direct navigation).
  // Not mid-slide, not during a programmatic slideTo, and not while waiting
  // for the parent to follow a change this slot announced itself. That last
  // one is the catalog title: its slide ends by navigating, and the catalog
  // tab fade keeps the old page — so the old activeIndex — on screen for
  // 150ms after navigate. Syncing in that window flipped the title back to
  // the page being left, then forward again when the swap landed.
  useEffect(() => {
    if (!(slot3d || stacked)) return;
    if (activeIndex === displayIndex) {
      // The parent has caught up (or never left): the wait, if any, is over.
      if (selfNavigated.current) {
        selfNavigated.current = false;
        clearTimeout(selfNavTimeout.current);
      }
      return;
    }
    if (slideCooldown.current || programmaticSlide.current || selfNavigated.current) return;
    setDisplayIndex(activeIndex);
  }, [activeIndex, slot3d, stacked, displayIndex]);

  // Tell the parent the slot moved, then wait for activeIndex to follow (the
  // sync effect above). If it never does — the parent declined, or went
  // somewhere else — give up after the grace period and show what it says.
  // Never during a programmatic slideTo, which is visual only.
  const announce = useCallback((next) => {
    if (programmaticSlide.current) return;
    selfNavigated.current = true;
    clearTimeout(selfNavTimeout.current);
    selfNavTimeout.current = setTimeout(() => {
      selfNavigated.current = false;
      if (slideCooldown.current) return; // a newer slide will announce again
      setDisplayIndex((shown) => (shown === activeIndexRef.current ? shown : activeIndexRef.current));
    }, SELF_NAV_GRACE_MS);
    onChangeIndex(next);
  }, [onChangeIndex]);

  // Re-check hover after page change (browser doesn't re-fire :hover on re-render)
  useEffect(() => {
    if (!slot3d || !outerRef.current) return;
    const still = outerRef.current.matches(":hover");
    setIsHovered(still);
  }, [displayIndex, slot3d]);

  const triggerSlide = useCallback((dir, axis = "y", ms = null) => {
    if (slideCooldown.current) return;
    slideCooldown.current = true;

    // Flash the arrow matching scroll direction
    setFlashDir(dir < 0 ? "up" : "down");
    if (flashTimeout.current) clearTimeout(flashTimeout.current);

    // Touch devices never fire mouseleave, so they would otherwise stay in
    // the hideArrows=true state forever after the first swipe. On touch we
    // skip the hide-after-click entirely; on desktop the 200ms hide still
    // runs and clears on mouseleave as before.
    const isTouch = isTouchDevice();
    if (hideArrowsTimeout.current) clearTimeout(hideArrowsTimeout.current);
    if (!isTouch) {
      // Hide arrows 200ms after click, before new title lands
      hideArrowsTimeout.current = setTimeout(() => setHideArrows(true), 200);
    } else {
      setHideArrows(false);
    }

    const from = displayIndex;
    const next = ((from + dir) % filters.length + filters.length) % filters.length;
    slideIdRef.current++;

    if (stacked) {
      // Stacked: update displayIndex immediately so CSS transition animates
      const slideMs = ms || SLIDE_MS;
      setSlideOverrideMs(ms || null);
      setDisplayIndex(next);
      flashTimeout.current = setTimeout(() => setFlashDir(null), Math.min(200, slideMs));
      setTimeout(() => {
        setSlideOverrideMs(null);
        announce(next);
        slideCooldown.current = false;
        if (isTouch) setHideArrows(false);
      }, slideMs);
    } else {
      setSlide({ to: next, dir, axis, id: slideIdRef.current });
      const baseDelay = slideMsProp || SLIDE_MS;
      const delay = axis === "fade" ? 250 : baseDelay;
      // Clear flash before slide lands
      flashTimeout.current = setTimeout(() => setFlashDir(null), delay * 0.5);
      // announce() before the batched setDisplayIndex flushes, so the sync
      // effect that follows already knows the parent is being told.
      setTimeout(() => {
        setDisplayIndex(next);
        setSlide(null);
        announce(next);
        slideCooldown.current = false;
        if (isTouch) setHideArrows(false);
      }, delay);
    }
  }, [displayIndex, filters.length, announce, stacked, slideMsProp]);

  // Keep latest refs for programmatic slideTo
  const triggerSlideRef = useRef(triggerSlide);
  triggerSlideRef.current = triggerSlide;
  const displayIndexRef = useRef(displayIndex);
  displayIndexRef.current = displayIndex;

  // Expose slideTo for programmatic step-by-step animation (visual only, no onChangeIndex)
  useImperativeHandle(ref, () => ({
    slideTo(targetIndex) {
      const current = displayIndexRef.current;
      if (targetIndex === current) return Promise.resolve();
      const dir = targetIndex > current ? 1 : -1;
      const steps = Math.abs(targetIndex - current);
      programmaticSlide.current = true;
      return new Promise((resolve) => {
        let i = 0;
        const step = () => {
          if (i >= steps) { programmaticSlide.current = false; resolve(); return; }
          i++;
          triggerSlideRef.current(dir, stacked ? "y" : horizontalOnly ? "fade" : "y");
          setTimeout(step, SLIDE_MS + 50);
        };
        step();
      });
    },
    get displayIndex() { return displayIndexRef.current; },
    triggerSlide(dir, ms) { triggerSlideRef.current(dir, "y", ms); },
  }), [stacked, horizontalOnly]);

  // Slide wheel handler (disabled when horizontalOnly, or when the parent
  // drives the slot from its own wheel handler via wheelNav={false}).
  // One gesture = one slide, via the shared wheel gate (utils/wheelInput):
  //   trackpad — locked for the whole burst (finger + inertia tail) until the
  //     stream goes silent, so a hard flick never fires twice; a deliberate
  //     second swipe mid-tail (sharp re-acceleration / direction flip after
  //     the slide animation) still registers.
  //   mouse — releases on animation end or a short silence, so rapid
  //     intentional notches still step through.
  useEffect(() => {
    if (!wheelNav || !(slot3d || stacked) || !enabled || horizontalOnly) return;
    const el = containerRef.current;
    if (!el) return;
    const slideMs = (slideMsProp || SLIDE_MS) + 20;
    const gate = createWheelGate({
      trackpadLockMs: slideMs,
      mouseSilenceMs: 160,
      mouseReleaseMs: slideMs,
    });
    const onWheel = (e) => {
      e.preventDefault();
      if (!gate.accept(e)) return;
      const dir = e.deltaY > 0 ? 1 : -1;
      if (stacked) {
        const next = displayIndex + dir;
        if (next < 0 || next >= filters.length) return;
      }
      triggerSlide(dir);
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => {
      el.removeEventListener("wheel", onWheel);
      gate.reset();
    };
  }, [wheelNav, slot3d, stacked, enabled, triggerSlide, horizontalOnly, displayIndex, filters.length, slideMsProp]);

  // Slide touch/swipe handler
  const touchStart = useRef({ x: 0, y: 0 });
  const handleTouchStart = useCallback((e) => {
    touchStart.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
  }, []);
  const handleTouchEnd = useCallback((e) => {
    if (!(slot3d || stacked) || !enabled) return;
    const dx = touchStart.current.x - e.changedTouches[0].clientX;
    const dy = touchStart.current.y - e.changedTouches[0].clientY;
    if (stacked && horizontalOnly) {
      // Stacked horizontal: horizontal swipes
      if (Math.abs(dx) > 25 && Math.abs(dx) > Math.abs(dy)) {
        const dir = dx > 0 ? 1 : -1;
        const next = displayIndex + dir;
        if (next >= 0 && next < filters.length) triggerSlide(dir, "y");
      }
    } else if (stacked) {
      // Stacked vertical: vertical swipes only
      if (Math.abs(dy) > 25) {
        const dir = dy > 0 ? 1 : -1;
        const next = displayIndex + dir;
        if (next >= 0 && next < filters.length) triggerSlide(dir, "y");
      }
    } else if (horizontalOnly) {
      if (Math.abs(dx) > 25 && Math.abs(dx) > Math.abs(dy)) {
        triggerSlide(dx > 0 ? 1 : -1, "fade");
      }
    } else {
      if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 25) {
        triggerSlide(dx > 0 ? 1 : -1, "x");
      } else if (Math.abs(dy) > 25) {
        triggerSlide(dy > 0 ? 1 : -1, "y");
      }
    }
  }, [slot3d, stacked, enabled, triggerSlide, horizontalOnly, displayIndex, filters.length]);

  // ── Scroll-based logic (non-3D) ──
  const repeatCount = 3;
  const totalItems = filters.length * repeatCount;
  const middleOffset = filters.length;

  const scrollToLogicalIndex = useCallback((index) => {
    if (slot3d) return;
    const el = scrollRef.current;
    if (!el) return;
    programmatic.current = true;
    el.scrollTop = itemHeight + (middleOffset + index) * itemHeight;
    requestAnimationFrame(() => { programmatic.current = false; });
  }, [middleOffset, itemHeight, slot3d]);

  useLayoutEffect(() => {
    if (!slot3d) scrollToLogicalIndex(activeIndex);
  }, [activeIndex, scrollToLogicalIndex, slot3d]);

  const handleScroll = useCallback(() => {
    if (programmatic.current || !enabled || instant || slot3d) return;

    setIsScrolling(true);
    if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
    scrollTimeoutRef.current = setTimeout(() => setIsScrolling(false), 300);

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      debounceRef.current = null;
      const el = scrollRef.current;
      if (!el) return;

      const rawIndex = Math.round((el.scrollTop - itemHeight) / itemHeight);
      const logicalIndex = ((rawIndex % filters.length) + filters.length) % filters.length;

      programmatic.current = true;
      el.scrollTop = itemHeight + (middleOffset + logicalIndex) * itemHeight;
      requestAnimationFrame(() => { programmatic.current = false; });

      if (logicalIndex !== activeIndex) {
        onChangeIndex(logicalIndex);
      }
    }, DEBOUNCE_MS);
  }, [activeIndex, filters.length, itemHeight, onChangeIndex, middleOffset, enabled, slot3d]);

  useEffect(() => {
    if (slot3d) return;
    const el = scrollRef.current;
    if (!el) return;
    if (!enabled) {
      const preventScroll = (e) => e.preventDefault();
      el.addEventListener("wheel", preventScroll, { passive: false });
      el.addEventListener("touchmove", preventScroll, { passive: false });
      return () => {
        el.removeEventListener("wheel", preventScroll);
        el.removeEventListener("touchmove", preventScroll);
      };
    }
  }, [enabled, slot3d]);

  const instantCooldown = useRef(false);
  useEffect(() => {
    if (!instant || !enabled || slot3d) return;
    const el = scrollRef.current;
    if (!el) return;
    const handleWheel = (e) => {
      e.preventDefault();
      if (instantCooldown.current) return;
      instantCooldown.current = true;
      setTimeout(() => { instantCooldown.current = false; }, 400);

      const direction = e.deltaY > 0 ? 1 : -1;
      const nextIndex = ((activeIndex + direction) % filters.length + filters.length) % filters.length;
      setIsScrolling(true);
      onChangeIndex(nextIndex);
      setTimeout(() => setIsScrolling(false), 300);
    };
    el.addEventListener("wheel", handleWheel, { passive: false });
    return () => el.removeEventListener("wheel", handleWheel);
  }, [instant, enabled, activeIndex, filters.length, onChangeIndex, slot3d]);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
      if (flashTimeout.current) clearTimeout(flashTimeout.current);
      if (hideArrowsTimeout.current) clearTimeout(hideArrowsTimeout.current);
      if (selfNavTimeout.current) clearTimeout(selfNavTimeout.current);
    };
  }, []);

  // Measure sizer width and apply it explicitly for smooth transitions
  useLayoutEffect(() => {
    if (slot3d && sizerRef.current) {
      setContainerWidth(sizerRef.current.scrollWidth);
    }
  }, [slot3d, displayIndex, slide]);

  // And again once the web fonts have arrived. The first measurement can
  // run in the fallback face: the phone header's wheel is set in Inter,
  // which is wider than its Arial fallback, so on a cold load (a phone on a
  // slow connection, mostly) the box came out too narrow and, with the
  // header wheel's overflow hidden, clipped the label until the next slide.
  useEffect(() => {
    if (!slot3d || typeof document === "undefined" || !document.fonts) return undefined;
    let live = true;
    const remeasure = () => {
      if (live && sizerRef.current) setContainerWidth(sizerRef.current.scrollWidth);
    };
    document.fonts.ready.then(remeasure).catch(() => {});
    document.fonts.addEventListener?.("loadingdone", remeasure);
    return () => {
      live = false;
      document.fonts.removeEventListener?.("loadingdone", remeasure);
    };
  }, [slot3d]);

  // Stacked mode: measure the labels so the wrapper can size itself and the
  // arrows can sit against whichever title is on screen. The stack renders
  // EVERY filter and its container is absolutely positioned, so the wrapper
  // has no in-flow width of its own.
  //
  // Two different widths come out of this, and the difference matters:
  //   stackWidth  — the WIDEST label, so the wrapper's box (and therefore the
  //                 hover target over it) is the same size whatever label is
  //                 showing. Sizing it to the active label instead meant that
  //                 swapping the title to a caret on hover shrank the very
  //                 area being hovered, the pointer fell outside, the title
  //                 came back, the box grew, and it re-entered — the title
  //                 visibly flickering between the caret and its name.
  //   activeWidth — the label actually on screen, published as a custom
  //                 property so the arrows alone travel to meet it.
  // Both are measured with a Range over the item's TEXT rather than its box:
  // the items stretch to the stack, so their boxes are all the widest label
  // while a Range reports the glyphs' own width.
  //
  // The widest is remembered per filter SET (keys, not labels) and only grows.
  // A caret swap keeps the same keys, so the wrapper holds the width it had
  // for the real titles; switching to a search query changes the keys and
  // re-measures from scratch.
  //
  // So does a web font landing. The first measurement can run in the
  // fallback face, and a title face narrower than its fallback left the
  // wrapper at the fallback's width, which only-grows then kept: the stack
  // is centred in the wrapper, so the title sat ~20px right of the count
  // under it. Skipped while hovered, where a caret may be what is showing.
  const widestRef = useRef({ sig: "", width: 0 });
  const [fontEpoch, setFontEpoch] = useState(0);
  useEffect(() => {
    if (!stacked || typeof document === "undefined" || !document.fonts) return undefined;
    let live = true;
    const remeasure = () => {
      if (!live || outerRef.current?.matches(":hover")) return;
      widestRef.current = { sig: "", width: 0 };
      setFontEpoch((n) => n + 1);
    };
    document.fonts.ready.then(remeasure).catch(() => {});
    document.fonts.addEventListener?.("loadingdone", remeasure);
    return () => {
      live = false;
      document.fonts.removeEventListener?.("loadingdone", remeasure);
    };
  }, [stacked]);

  useLayoutEffect(() => {
    if (!stacked || !stackRef.current) return;
    const measure = (el) => {
      try {
        const range = document.createRange();
        range.selectNodeContents(el);
        return range.getBoundingClientRect().width;
      } catch {
        return el.getBoundingClientRect().width;
      }
    };
    const widths = Array.from(stackRef.current.children).map(measure);
    if (!widths.length) return;

    const sig = filters.map((f) => f.key).join("|");
    if (widestRef.current.sig !== sig) widestRef.current = { sig, width: 0 };
    const widest = Math.max(widestRef.current.width, ...widths);
    if (widest > 0) {
      widestRef.current.width = widest;
      setStackWidth(widest);
    }

    const active = widths[displayIndex];
    if (active > 0) setActiveWidth(active);
  }, [stacked, displayIndex, filters, fontEpoch]);

  const padding = itemHeight;

  const classNames = [
    "slot-filter",
    className,
    slot3d && !stacked && "slot-filter--slide",
    stacked && "slot-filter--stacked",
    isScrolling && "slot-filter--scrolling",
    enabled && "slot-filter--enabled",
    showBorder && "slot-filter--border",
    loading && "slot-filter--loading",
  ].filter(Boolean).join(" ");

  // ── Stacked list render ──
  if (stacked) {
    const stackItemHeight = itemHeight;
    const isHorizontalStack = horizontalOnly;
    const offset = isHorizontalStack
      ? -(displayIndex * 100)
      : -(displayIndex * stackItemHeight);
    const transformProp = isHorizontalStack
      ? `translateX(${offset}%)`
      : `translateY(${offset}px)`;

    return (
      <div
        className={`slot-filter__outer slot-filter__outer--stacked${isHorizontalStack ? " slot-filter__outer--stacked-horizontal" : ""}${stuck ? " slot-filter__outer--stacked-stuck" : ""}${isHovered ? " slot-filter__outer--hovered" : ""}`}
        ref={outerRef}
        style={stackWidth ? { width: stackWidth, "--slot-active-w": `${activeWidth ?? stackWidth}px` } : undefined}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => { setIsHovered(false); setHideArrows(false); }}
      >
        <div
          ref={containerRef}
          className={classNames}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          <div
            ref={stackRef}
            className={`slot-filter__stack${isHorizontalStack ? " slot-filter__stack--horizontal" : ""}`}
            style={{ transform: transformProp, transition: `transform ${(slideOverrideMs || 350) / 1000}s cubic-bezier(0.22, 1, 0.36, 1)` }}
          >
            {filters.map((filter, i) => {
              const isBefore = i < displayIndex;
              const isAfter = i > displayIndex;
              const parkOffset = !isHorizontalStack && stuck && isBefore
                ? -(displayIndex - i) * stackItemHeight * 0.6
                : 0;
              return (
                <div
                  key={filter.key}
                  className={`slot-filter__item slot-filter__stack-item${i === displayIndex ? " slot-filter__stack-item--active" : ""}${!isHorizontalStack && stuck && isBefore ? " slot-filter__stack-item--parked" : ""}${showInitialSlide && i === displayIndex ? " slot-filter__stack-item--rise-in" : ""}`}
                  style={{
                    height: stackItemHeight,
                    ...(isHorizontalStack ? { minWidth: "100%" } : {}),
                    transform: `translateY(${parkOffset}px)`,
                    opacity: stuck && (isBefore || isAfter) ? 0 : 1,
                    transition: `transform ${(slideOverrideMs || 350) / 1000}s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.18s ease`,
                  }}
                  onClick={() => {
                    if (i === displayIndex && onClickCurrent) {
                      onClickCurrent();
                    } else if (i !== displayIndex) {
                      triggerSlide(i - displayIndex, "y");
                    }
                  }}
                >
                  {filter.label}
                </div>
              );
            })}
          </div>
        </div>
        <span className={`slot-filter__arrows${horizontalOnly ? " slot-filter__arrows--horizontal" : ""}${hideArrows && !flashDir ? " slot-filter__arrows--hidden" : ""}${flashDir ? " slot-filter__arrows--flash" : ""}`}>
          {/* Only rendered when there is somewhere to go: this list does not
              wrap (the click below is already bounds-guarded), so on the first
              page an up caret points at nothing. */}
          {displayIndex > 0 && (
          <button type="button" className={`slot-filter__arrow slot-filter__arrow--${horizontalOnly ? "left" : "up"}${flashDir === "up" ? " slot-filter__arrow--flash" : ""}`}
            onClick={(e) => { e.stopPropagation(); if (displayIndex > 0) triggerSlide(-1, "y"); }} aria-label="Previous">
            {horizontalOnly ? (
              <svg width="8" height="14" viewBox="0 0 8 14" fill="none"
                stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M7 1L1 7L7 13" />
              </svg>
            ) : (
              <svg width="14" height="8" viewBox="0 0 14 8" fill="none"
                stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M1 7L7 1L13 7" />
              </svg>
            )}
          </button>
          )}
          {displayIndex < filters.length - 1 && (
          <button type="button" className={`slot-filter__arrow slot-filter__arrow--${horizontalOnly ? "right" : "down"}${flashDir === "down" ? " slot-filter__arrow--flash" : ""}`}
            onClick={(e) => { e.stopPropagation(); if (displayIndex < filters.length - 1) triggerSlide(1, "y"); }} aria-label="Next">
            {horizontalOnly ? (
              <svg width="8" height="14" viewBox="0 0 8 14" fill="none"
                stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M1 1L7 7L1 13" />
              </svg>
            ) : (
              <svg width="14" height="8" viewBox="0 0 14 8" fill="none"
                stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M1 1L7 7L13 1" />
              </svg>
            )}
          </button>
          )}
        </span>
      </div>
    );
  }

  // ── Slide render ──
  if (slot3d) {
    const dirClass = slide
      ? slide.axis === "fade"
        ? "fade"
        : slide.axis === "x"
          ? (slide.dir > 0 ? "left" : "right")
          : (slide.dir > 0 ? "up" : "down")
      : null;

    return (
      <div
        className={`slot-filter__outer${isHovered ? " slot-filter__outer--hovered" : ""}`}
        ref={outerRef}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => { setIsHovered(false); setHideArrows(false); }}
      >
        <div
          ref={containerRef}
          className={classNames}
          style={containerWidth ? { width: containerWidth } : undefined}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          {/* Hidden sizer: always targets the incoming text for immediate resize */}
          <div ref={sizerRef} className="slot-filter__item slot-filter__sizer" aria-hidden="true">
            {(filters[slide ? slide.to : displayIndex] || filters[0] || {}).label}
          </div>
          {slide ? (
            <>
              <div
                key={`out-${slide.id}`}
                className={`slot-filter__item slot-filter__face slot-filter__face--out-${dirClass}`}
              >
                {filters[displayIndex].label}
              </div>
              <div
                key={`in-${slide.id}`}
                className={`slot-filter__item slot-filter__face slot-filter__face--in-${dirClass}`}
              >
                {filters[slide.to].label}
              </div>
            </>
          ) : (
            <div
              className={`slot-filter__item slot-filter__face${showInitialSlide ? " slot-filter__face--in-up" : ""}`}
            >
              <span className="slot-filter__label" onClick={onClickCurrent}>{filters[displayIndex].label}</span>
            </div>
          )}
        </div>
        <span className={`slot-filter__arrows${horizontalOnly ? " slot-filter__arrows--horizontal" : ""}${hideArrows && !flashDir ? " slot-filter__arrows--hidden" : ""}${flashDir ? " slot-filter__arrows--flash" : ""}`}>
          <button type="button" className={`slot-filter__arrow slot-filter__arrow--${horizontalOnly ? "left" : "up"}${flashDir === "up" ? " slot-filter__arrow--flash" : ""}`}
            onClick={(e) => { e.stopPropagation(); triggerSlide(-1, horizontalOnly ? "fade" : "y"); }} aria-label="Previous">
            {horizontalOnly ? (
              <svg width="8" height="14" viewBox="0 0 8 14" fill="none"
                stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M7 1L1 7L7 13" />
              </svg>
            ) : (
              <svg width="14" height="8" viewBox="0 0 14 8" fill="none"
                stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M1 7L7 1L13 7" />
              </svg>
            )}
          </button>
          <button type="button" className={`slot-filter__arrow slot-filter__arrow--${horizontalOnly ? "right" : "down"}${flashDir === "down" ? " slot-filter__arrow--flash" : ""}`}
            onClick={(e) => { e.stopPropagation(); triggerSlide(1, horizontalOnly ? "fade" : "y"); }} aria-label="Next">
            {horizontalOnly ? (
              <svg width="8" height="14" viewBox="0 0 8 14" fill="none"
                stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M1 1L7 7L1 13" />
              </svg>
            ) : (
              <svg width="14" height="8" viewBox="0 0 14 8" fill="none"
                stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M1 1L7 7L13 1" />
              </svg>
            )}
          </button>
        </span>
      </div>
    );
  }

  // ── Scroll-based render (default) ──
  return (
    <div className={classNames}>
      <div className="slot-filter__window" />
      <div
        ref={scrollRef}
        className="slot-filter__scroll"
        onScroll={handleScroll}
      >
        <div style={{ height: padding }} />
        {Array.from({ length: totalItems }, (_, i) => {
          const logicalI = i % filters.length;
          const filter = filters[logicalI];
          const isActive = logicalI === activeIndex;
          return (
            <div
              key={`${filter.key}-${i}`}
              className={`slot-filter__item${isActive ? " slot-filter__item--active" : ""}`}
              style={{ height: itemHeight }}
            >
              {filter.label}
            </div>
          );
        })}
        <div style={{ height: padding }} />
      </div>
    </div>
  );
});

export default SlotFilter;
